import type { SoundVariant } from '../themes/types';

// Named sound preset library for the theme editor's sound picker.
//
// Each entry is a single `SoundVariant` (persisted as-is into the custom
// theme's `sound.tap.variants` / `sound.celebration.variants`). The editor
// composes multi-variant clips by concatenating entries — the library
// itself intentionally stays a flat catalog.
//
// Adding a preset = one entry here. Callers see the union via
// `SOUND_LIBRARY` / `SOUND_LIBRARY_BY_KEY`.

export type SoundLibraryKey = 'DING' | 'CHIME' | 'POP' | 'SQUEAK' | 'POOF' | 'SAMPLE_POP' | 'NONE';

export interface SoundLibraryEntry {
  key: SoundLibraryKey;
  name: string; // Finnish label shown in the picker
  variant: SoundVariant;
  previewLabel: string; // "Kuuntele" is the button; this labels the row for a11y
}

export const SOUND_LIBRARY: readonly SoundLibraryEntry[] = [
  {
    key: 'DING',
    name: 'Kilinä',
    variant: { kind: 'chord', notes: ['E5', 'A5'] },
    previewLabel: 'Kuuntele kilinä',
  },
  {
    key: 'CHIME',
    name: 'Sointu',
    variant: { kind: 'chord', notes: ['C5', 'E5', 'G5'] },
    previewLabel: 'Kuuntele sointu',
  },
  {
    key: 'POP',
    name: 'Pop',
    variant: { kind: 'note', note: 'C4' },
    previewLabel: 'Kuuntele pop',
  },
  {
    key: 'SQUEAK',
    name: 'Vinkaisu',
    variant: { kind: 'note', note: 'A6' },
    previewLabel: 'Kuuntele vinkaisu',
  },
  {
    key: 'POOF',
    name: 'Puhallus',
    variant: { kind: 'chord', notes: ['C3', 'G3'] },
    previewLabel: 'Kuuntele puhallus',
  },
  {
    key: 'SAMPLE_POP',
    name: 'Poks',
    variant: { kind: 'sample', url: '/sounds/pop.wav' },
    previewLabel: 'Kuuntele poks',
  },
  {
    key: 'NONE',
    name: 'Hiljainen',
    variant: { kind: 'chord', notes: [] },
    previewLabel: 'Hiljainen (ei ääntä)',
  },
];

export const SOUND_LIBRARY_BY_KEY: Readonly<Record<SoundLibraryKey, SoundLibraryEntry>> =
  Object.fromEntries(SOUND_LIBRARY.map((e) => [e.key, e])) as Record<
    SoundLibraryKey,
    SoundLibraryEntry
  >;
